<template>
  <div class="bill-list-container">
    <PremiumPage
      title="Bills"
      subtitle="View and manage individual house bills"
    >
      <div class="pa-6">
        <!-- ── Stats Row ─────────────────────────── -->
        <div class="stats-grid mb-6">
          <PremiumStatCard
            label="Total Billed"
            :value="formatCurrency(summary.total_billed)"
            icon="mdi-receipt-text-outline"
            color="#3B82F6"
            :loading="loading"
          />
          <PremiumStatCard
            label="Collected"
            :value="formatCurrency(summary.collected)"
            icon="mdi-check-circle-outline"
            color="#10B981"
            :loading="loading"
          />
          <PremiumStatCard
            label="Pending"
            :value="formatCurrency(summary.pending)"
            icon="mdi-clock-outline"
            color="#F59E0B"
            :loading="loading"
          />
          <PremiumStatCard
            label="Overdue"
            :value="formatCurrency(summary.overdue)"
            icon="mdi-alert-circle-outline"
            color="#EF4444"
            :loading="loading"
          />
        </div>

        <!-- ── Premium Table ─────────────────────── -->
        <PremiumTable
          title="Member Bills"
          :columns="columns"
          :items="bills"
          :loading="loading"
          :total-items="totalRecords"
          :page.sync="page"
          :items-per-page.sync="itemsPerPage"
          :searchable="true"
          @search="onSearch"
        >
          <!-- Header Extra: Date Filter + Export -->
          <template #header-extra>
            <div class="header-controls">
              <!-- Status Filter -->
              <FilterDropdown
                v-model="paymentStatus"
                :options="paymentStatusOptions"
                item-text="text"
                item-value="value"
                placeholder="All Status"
                all-label="All Status"
                @change="page = 1; getBills();"
              />

              <!-- Month Picker: rely on v-model watcher to trigger getBills -->
              <PremiumDatePicker
                v-model="date"
                type="month"
                placeholder="Select Month"
              />

              <!-- Export Button -->
              <button
                class="export-btn"
                :style="{ background: brandColor }"
                title="Export to Excel"
                @click="exportDialog = true"
              >
                <v-icon size="16" color="white">mdi-download-outline</v-icon>
              </button>
            </div>
          </template>

          <!-- Custom Body Columns -->
          <template #col-member_name="{ item }">
            <div class="member-cell">
              <span class="member-name">{{ item.member_name }}</span>
            </div>
          </template>

          <template #col-flat_house_number="{ item }">
            <div class="house-number-badge">
              <v-icon size="14" color="#94A3B8" v-if="item.unit_type === 1">
                mdi-home-outline
              </v-icon>
              <v-icon size="14" color="#94A3B8" v-else-if="item.unit_type === 2">
                mdi-store-outline
              </v-icon>
              <span v-if="item.member_flat" class="flat-prefix"
                >{{ item.member_flat }}-</span
              >
              <span class="house-num">{{ item.member_house }}</span>
            </div>
          </template>

          <template #col-title="{ item }">
            <span class="bill-title-text">{{ item.title }}</span>
          </template>

          <template #col-payable_amount="{ item }">
            <span class="amount-text"
              >₹{{ Number(item.payable_amount).toLocaleString() }}</span
            >
          </template>

          <template #col-created_at="{ item }">
            <span class="date-text">{{
              item.created_at | formatDateTime
            }}</span>
          </template>


        <!-- <template #col-payment_status="{ item }">
          <div class="status-pill-wrap">
            <div
              class="status-pill-btn"
              :class="item.is_cancelled ? 'status-cancelled' : (item.payment_status == 0 ? 'status-pending' : 'status-completed')"
              @click.stop="toggleStatusMenu($event, item)"
              style="cursor: pointer;"
            >
            <span class="dot"></span>
            
              {{ item.is_cancelled ? 'Cancelled' : (item.payment_status == 0 ? "Pending" : "Completed") }}
              <v-icon size="12" class="ml-1">mdi-chevron-down</v-icon>
            </div>
          </div>
        </template> -->
        <template #col-payment_status="{ item }">
          <div class="status-pill-wrap">
            <div
              class="status-pill-btn"
              :class="
                item.is_cancelled
                  ? 'status-cancelled'
                  : item.payment_status == 0
                    ? 'status-pending'
                    : item.payment_status == 2
                      ? 'status-approval-request'
                      : 'status-completed'
              "
              @click.stop="toggleStatusMenu($event, item)"
              :style="{ cursor: item.payment_status == 2 ? 'default' : 'pointer' }"
            >
              <span class="dot"></span>

              {{
                item.is_cancelled
                  ? 'Cancelled'
                  : item.payment_status == 0
                    ? 'Pending'
                    : item.payment_status == 2
                      ? 'Approval Request'
                      : 'Completed'
              }}

              <v-icon v-if="item.payment_status != 2" size="12" class="ml-1">mdi-chevron-down</v-icon>
            </div>
          </div>
        </template>

          <template #col-paid_date="{ item }">
            <div class="paid-date-cell">
              <span class="date-text" v-if="item.payment_status != 0">{{
                item.paid_date | formatDateTime
              }}</span>
              <!-- <span v-else class="dash">—</span> -->

              <button
                v-if="item.bill_document"
                class="doc-view-btn"
                title="View Document"
                @click="openDocument(item.bill_document)"
              >
                <v-icon size="14">mdi-file-eye-outline</v-icon>
              </button>
      
            </div>
          </template>

          <template #col-actions="{ item }">
             <div class="actions-cell">


            <ActionButtons
              v-if="item.payment_status == 0 && !item.is_cancelled "
              :show-edit="true"
              :show-reject="true"
              edit-label="Edit Bill"
              reject-label="Cancel Bill"
              @edit="openEditBill(item)"
              @reject="openCancelBill(item)"
            />
            <!-- Approval Request: show review icon -->
            <v-tooltip
              v-if="item.payment_status == 2"
              top
              :open-delay="200"
              content-class="action-tooltip"
            >
              <template v-slot:activator="{ on, attrs }">
                <button
                  class="btn-icon-approve"
                  v-bind="attrs"
                  v-on="on"
                  @click="openApprovalModal(item)"
                  @mouseenter="item._hoverApprove = true"
                  @mouseleave="item._hoverApprove = false"
                >
                  <v-icon size="20" color="#10B981">mdi-check</v-icon>
                  <div
                    class="hover-bg"
                    :style="{ backgroundColor: item._hoverApprove ? 'rgba(16, 185, 129, 0.12)' : 'transparent' }"
                  ></div>
                </button>
              </template>
              <span>Approve Bill</span>
            </v-tooltip>

            <v-tooltip
              v-if="item.payment_status == 2"
              top
              :open-delay="200"
              content-class="action-tooltip"
            >
              <template v-slot:activator="{ on, attrs }">
                <button
                  class="btn-icon-approve"
                  style="margin-left: 8px;"
                  v-bind="attrs"
                  v-on="on"
                  @click="openRejectModal(item)"
                  @mouseenter="item._hoverReject = true"
                  @mouseleave="item._hoverReject = false"
                >
                  <v-icon size="20" color="#EF4444">mdi-close</v-icon>
                  <div
                    class="hover-bg"
                    :style="{ backgroundColor: item._hoverReject ? 'rgba(239, 68, 68, 0.12)' : 'transparent' }"
                  ></div>
                </button>
              </template>
              <span>Reject Bill</span>
            </v-tooltip>
            <button
                v-if="item.receipt_path && item.payment_status === 1"
                class="btn-icon-ghost"
                title="Download Receipt"
                @click="downloadReceipt(item.receipt_path)"
              >
                <v-icon size="18" color="#10b981">mdi-tray-arrow-down</v-icon>
            </button>
             </div>

          </template>
          
        </PremiumTable>



        <!-- ── Export Confirmation ──────────────── -->
        <ConfirmModal
          v-model="exportDialog"
          type="primary"
          :btnColor="brandColor"
          title="Export to Excel"
          message="Are you sure you want to export the member bills to an Excel file?"
          confirm-text="Yes, Export"
          @confirm="
            exportToExcel();
            exportDialog = false;
          "
        />

        <!-- ── Reject Payment Confirmation ──────────────── -->
        <ConfirmModal
          v-model="rejectConfirmModal.open"
          type="danger"
          title="Reject Payment Request"
          message="Are you sure you want to reject this payment request? The bill will be set back to Pending."
          confirm-text="Yes, Reject"
          cancel-text="Cancel"
          :loading="rejectConfirmModal.loading"
          @confirm="submitRejectPayment"
        />

        <!-- ── Payment Details Modal ──────────────── -->
       <PremiumModal
          v-model="paymentModal.open"
          title="Enter Payment Details"
          submit-text="Confirm Payment"
          cancel-text="Cancel"
          :max-width="440"
          :persistent="true"
          @submit="submitPaymentModal"
          @close="paymentModal.open = false"
        >
          <div style="margin-bottom: 16px;">
            <PremiumInput
              v-model="paymentModal.ref_id"
              label="Reference ID"
              placeholder="Enter transaction / reference ID"
            />
          </div>
 
          <div style="margin-bottom: 4px;">
            <PremiumSelect
              v-model="paymentModal.payment_method"
              label="Payment Method"
              :items="paymentMethods"
              placeholder="Select payment method"
              :error="paymentErrors.payment_method"
            />
            <div v-if="paymentErrors.payment_method" class="field-error">
              {{ paymentErrors.payment_method }}
            </div>
          </div>
 
          <div style="margin-bottom: 4px; margin-top: 12px;">
            <PremiumInput
              v-model="paymentModal.remarks"
              label="Remark"
              placeholder="Enter remark"
              :error="paymentErrors.remarks"
            />
            <div v-if="paymentErrors.remarks" class="field-error">
              {{ paymentErrors.remarks }}
            </div>
          </div>
 
          <div style="margin-top: 16px;">
            <PremiumDatePicker
              v-model="paymentModal.paid_date"
              label="Paid Date"
              placeholder="Select Date"
              :max="today"
              :clearable="false"
            />
          </div>
        </PremiumModal>

        <!-- ── Edit Bill Modal ──────────────── -->
        <PremiumModal
          v-model="editModal.open"
          title="Edit Generated Bill"
          submit-text="Update Bill"
          cancel-text="Cancel"
          :max-width="520"
          :persistent="true"
          @submit="submitEditBill"
          @close="editModal.open = false"
        >
          <!-- Bill info header -->
      
          <!-- Section: Basic -->
          <div class="edit-field-row">
                    <PremiumInput
              v-model="editModal.amount"
              label="Amount (₹)"
               placeholder="0"
              v-validate="'required'"
              name="name"
              data-vv-as="Name"
             :error="editErrors.amount"
             @input="editErrors.amount = ''"
            />

          

                    <PremiumDatePicker
              v-model="editModal.due_on" 
              placeholder="Select date"
              label="Due Date"
              :max="today"
              :clearable="false"
              :error="editErrors.due_on"
              @input="editErrors.due_on = ''"
            />


          
          </div>

          <!-- Section: Fees & Charges -->
          <div class="edit-field-row">

               <PremiumInput
            v-model="editModal.late_fee_amount"
              label="Late Fee (₹)"
               placeholder="0"
              v-validate="'required'"
               type="number"
              
             :error="editErrors.late_fee_amount"
             @input="editErrors.late_fee_amount = ''"
            />


        
                    <PremiumInput
            v-model="editModal.discount_amount"
              label="Discount (₹)"
               placeholder="0"
               type="number"
              
             :error="editErrors.discount_amount"
             @input="editErrors.discount_amount = ''"
            />
          </div>

          <!-- Section: Additional Charge -->
          <div class="edit-field-row">
                <PremiumInput
            v-model="editModal.additional_charge_amount"
              label="Charge Amount (₹)"
               placeholder="0"
               type="number"
              
             :error="editErrors.additional_charge_amount"
             @input="editErrors.additional_charge_amount = ''"
            />

            <PremiumInput
            v-model="editModal.additional_charge_description"
              label="Charge Description"
             placeholder="e.g. Maintenance penalty"
                 name="name"
             :error="editErrors.additional_charge_description"
             @input="editErrors.additional_charge_description = ''"
            />
          </div>
        </PremiumModal>

        <!-- ── Cancel Bill Modal ──────────────── -->
        <PremiumModal
          v-model="cancelModal.open"
          title="Cancel Generated Bill"
          submit-text="Confirm Cancel"
          cancel-text="Close"
          :max-width="400"
          :persistent="true"
          @submit="submitCancelBill"
          @close="cancelModal.open = false"
        >
          <div style="margin-bottom: 16px;">
            <label style="font-size: 14px; color: #4b5563;">Please provide a reason for cancelling this bill:</label>
          </div>
               <PremiumInput
                   v-model="cancelModal.reason"
                   placeholder="Cancellation Reason"
                  multiline
                  rows="4"
                  resizable
                  v-validate="'required'"
                  name="description"
                  :error="cancelModal.error"
                  @input="cancelModal.error = ''"
                />
        </PremiumModal>

     <!-- ── Approval Request Modal ──────────────── -->
      <PremiumModal
        v-model="approvalModal.open"
        title="Payment Approval Request"
        :submit-text="approvalModal.showRejectReason ? 'Confirm Reject' : 'Approve Payment'"
        cancel-text="Reject"
        :max-width="520"
        :persistent="true"
        :loading="approvalModal.loading"
        @submit="approvalModal.showRejectReason ? submitRejectPayment() : submitApprovePayment()"
        @close="onApprovalModalClose"
      >
        <div v-if="approvalModal.item" class="apv-body">

          <!-- Header badge row -->
          <div class="apv-header-badge">
            <div class="apv-badge-icon">
              <v-icon size="18" color="var(--brand)">mdi-shield-check-outline</v-icon>
            </div>
            <div>
              <p class="apv-badge-title">Payment approval request</p>
          
            </div>
          </div>

          <!-- Details 2x2 grid -->
          <div class="apv-grid">
            <div class="apv-mini-card">
              <span class="apv-mini-label">Member</span>
              <span class="apv-mini-value">{{ approvalModal.item.member_name || '—' }}</span>
            </div>
            <div class="apv-mini-card">
              <span class="apv-mini-label">Unit</span>
              <span class="apv-mini-value">
                <span v-if="approvalModal.item.member_flat">{{ approvalModal.item.member_flat }}-</span>{{ approvalModal.item.member_house || '—' }}
              </span>
            </div>
            <div class="apv-mini-card">
              <span class="apv-mini-label">Bill title</span>
              <span class="apv-mini-value">{{ approvalModal.item.title || '—' }}</span>
            </div>
            <div class="apv-mini-card">
              <span class="apv-mini-label">Amount</span>
              <span class="apv-mini-value apv-amount-green">₹{{ Number(approvalModal.item.payable_amount || 0).toLocaleString() }}</span>
            </div>
          </div>

          <!-- Member Remark -->
          <div class="apv-remark-row" v-if="approvalModal.item.payment_remark">
          
            <div>
              <span class="apv-mini-label">Member remark</span>
              <p class="apv-remark-text">{{ approvalModal.item.payment_remark }}</p>
            </div>
          </div>

          <!-- Reference ID -->
          <div class="apv-remark-row" v-if="approvalModal.item.payment_ref_id">
            <v-icon size="15" color="#9CA3AF" style="margin-top:1px; flex-shrink:0;">mdi-identifier</v-icon>
            <div>
              <span class="apv-mini-label">Reference ID</span>
              <p class="apv-remark-text">{{ approvalModal.item.payment_ref_id }}</p>
            </div>
          </div>

          <!-- Screenshot -->
          <div class="apv-screenshot-box" v-if="approvalModal.item.payment_screenshot">
            <div class="apv-screenshot-header">
              <div class="apv-screenshot-header-left">
                <v-icon size="14" color="#6B7280">mdi-image-outline</v-icon>
                <span class="apv-mini-label" style="margin:0;">Payment screenshot</span>
              </div>
            
            </div>
            <div class="apv-screenshot-preview" @click="viewScreenshot(approvalModal.item.payment_screenshot)">
              <img
                :src="base_url + '/' + approvalModal.item.payment_screenshot"
                class="apv-screenshot-img"
                alt="Payment Screenshot"
              />
              <div class="apv-screenshot-overlay">
                <v-icon color="white" size="24">mdi-magnify-plus-outline</v-icon>
                <span class="apv-zoom-label">Click to zoom</span>
              </div>
            </div>
          </div>

          <div class="apv-no-screenshot" v-else>
            <v-icon size="28" color="#CBD5E1">mdi-image-off-outline</v-icon>
            <span>No screenshot uploaded</span>
          </div>

          <!-- Reject Reason -->
          <div class="apv-reject-section" v-if="approvalModal.showRejectReason">
            <PremiumInput
              v-model="approvalModal.rejectReason"
              label="Rejection reason"
              placeholder="Enter reason for rejection..."
              multiline
              rows="3"
              :error="approvalModal.rejectError"
              @input="approvalModal.rejectError = ''"
            />
          </div>

        </div>
      </PremiumModal>

        <!-- ── Status Floating Dropdown ── -->
        <div
          v-if="statusMenu.open"
          class="status-dropdown-portal"
          :style="{ top: statusMenu.y + 'px', left: statusMenu.x + 'px' }"
          @click.stop
        >
          <div
            v-for="s in statusOptions"
            :key="s.value"
            class="status-option"
            :class="{ active: statusMenu.item && statusMenu.item.payment_status == s.value }"
            @click="selectStatus(statusMenu.item, s.value)"
          >
            <span class="status-dot" :style="{ background: s.value == 0 ? '#F59E0B' : '#10B981' }"></span>
            <span>{{ s.text }}</span>
            <v-icon v-if="statusMenu.item && statusMenu.item.payment_status == s.value" size="12" color="var(--brand)" class="ml-auto">mdi-check</v-icon>
          </div>
        </div>

        <!-- ── Media Viewer ── -->
        <MediaViewer
          v-model="imageDialog"
          :items="mediaItems_bill"
          :index="mediaIndex_bill"
        />
      </div>
    </PremiumPage>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import moment from "moment";
import PremiumPage from "../../common/PremiumPage.vue";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumDatePicker from "../../common/PremiumDatePicker.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import PremiumStatCard from "../../common/PremiumStatCard.vue";
import MediaViewer from "../../common/MediaViewer.vue";
import PremiumModal from "../../common/PremiumModal.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import ActionButtons from "../../common/ActionButtons.vue";
import PremiumSelect from "../../common/PremiumSelect.vue";
import FilterDropdown from "../../common/FilterDropdown.vue";
export default {
  name: "BillHouseList",

  components: {
    PremiumPage,
    PremiumTable,
    PremiumDatePicker,
    ConfirmModal,
    PremiumStatCard,
    MediaViewer,
    PremiumModal,
    PremiumInput,
    ActionButtons,
    PremiumSelect,
    FilterDropdown,
  },

  data() {
    return {
      bills: [],
      summary: {
        total_billed: 0,
        collected: 0,
        pending: 0,
        overdue: 0,
      },
      loading: false,
      billStatusDialog: false,
      houseBill_details: {},
      status: 0,
      search: "",
      page: 1,
      itemsPerPage: 10,
      totalRecords: 0,
      menu: false,
      date: null,
      paymentStatus: "",
      user: {},
      exportDialog: false,
      documents: [],
      base_url: process.env.VUE_APP_API_URL,
      statusMenu: { open: false, x: 0, y: 0, item: null },
      statusOptions: [
        { text: "Pending", value: 0 },
        { text: "Completed", value: 1 },
      ],
      imageDialog: false,
      mediaIndex_bill: 0,
      mediaItems_bill: [],
      paymentModal: { open: false, item: null, ref_id: '', payment_method: '', paid_date: null ,remarks: ''},
      paymentMethods: ['Cash',  'Cheque', 'Bank Transfer', 'Online', 'Other'],
      paymentErrors: { payment_method: '', remarks: '' },
      editModal: { open: false, item: null, amount: 0, due_on: null, late_fee_amount: 0, discount_amount: 0, additional_charge_amount: 0, additional_charge_description: '' },
      editErrors: { amount: '' },
      cancelModal: { open: false, item: null, reason: '', error: '' },
      approvalModal: {
        open: false,
        item: null,
        loading: false,
        showRejectReason: false,
        rejectReason: '',
        rejectError: '',
      },
      rejectConfirmModal: {
        open: false,
        item: null,
        loading: false,
      },
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor"]),
    brandColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },

    paymentStatusOptions() {
      return [
        { text: "Pending",          value: "0" },
        { text: "Completed",        value: "1" },
        { text: "Approval Request", value: "2" },
        { text: "Cancelled",        value: "3" },
      ];
    },

    today() {
      return moment().format('YYYY-MM-DD')
    },

    hasPermission() {
      if (this.user.role == "society") return true;
      if (this.user.role == "societyMember") {
        let permissions = this.user.permissions;
        if (permissions && permissions.includes("bills")) {
          return true;
        }
      }
      return false;
    },

    columns() {
      return [
        {
          key: "member_name",
          label: this.$t("houseBills.headers.member") || "Member",
          width: "240px",
        },
        {
          key: "flat_house_number",
          label: this.$t("houseBills.headers.flat_house_number") || "Unit",
          width: "140px",
        },
        {
          key: "title",
          label: this.$t("houseBills.headers.title") || "Bill Title",
          minWidth: "160px",
        },
        {
          key: "payable_amount",
          label: this.$t("houseBills.headers.payable_amount") || "Amount",
          width: "110px",
        },
        {
          key: "created_at",
          label: this.$t("houseBills.headers.date") || "Date",
          width: "140px",
        },
        {
          key: "payment_status",
          label: this.$t("houseBills.headers.status") || "Status",
          width: "177px",
        },
        {
          key: "paid_date",
          label: "Payment Info",
          width: "160px",
          align: "right",
        },
        {
          key: "actions",
          label: "Actions",
          width: "150px",
          align: "center",
        },
      ];
    },
  },

  watch: {
    date(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.$nextTick(() => {
        this.page = 1;
        this.getBills();
      });
    },
    page() {
      this.getBills();
    },
    itemsPerPage() {
      this.page = 1;
      this.getBills();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
      this.sessionStorage(val);
      this.page = 1;
      this.getBills();
    },

    openApprovalModal(item) {
      this.approvalModal = {
        open: true,
        item,
        loading: false,
        showRejectReason: false,
        rejectReason: '',
        rejectError: '',
      };
    },

    openRejectModal(item) {
      this.rejectConfirmModal = {
        open: true,
        item,
        loading: false,
      };
    },

    onApprovalModalClose() {
      // "Reject" button clicked — close approval modal and open reject confirmation
      this.approvalModal.open = false;
      if (this.approvalModal.item) {
        this.$nextTick(() => {
          this.rejectConfirmModal = {
            open: true,
            item: this.approvalModal.item,
            loading: false,
          };
        });
      }
    },

    viewScreenshot(filename) {
      this.mediaItems_bill = [{
        url: this.base_url + '/' + filename,
        type: 'image',
        name: filename,
      }];
      this.mediaIndex_bill = 0;
      this.imageDialog = true;
    },

    submitApprovePayment() {
      const item = this.approvalModal.item;
      this.approvalModal.loading = true;
      const formData = new FormData();
      formData.append('status', 1);
      formData.append('is_approval', 1);
      this.$axios.post(`societyadmin/bills/house/${item.id}/updateStatus`, formData)
        .then((res) => {
          this.approvalModal.loading = false;
          if (res.data.status) {
            this.approvalModal.open = false;
            this.setAlert({ showAlert: true, message: 'Payment approved successfully.', variant: 'green accent-4', timeout: 2000 });
            this.getBills();
          } else {
            this.setAlert({ showAlert: true, message: res.data.message, variant: 'red accent-3', timeout: 3000 });
          }
        }).catch(() => {
          this.approvalModal.loading = false;
          this.setAlert({ showAlert: true, message: 'Something went wrong.', variant: 'red accent-3', timeout: 3000 });
        });
    },

    submitRejectPayment() {
      const item = this.rejectConfirmModal.item || this.approvalModal.item;
      this.rejectConfirmModal.loading = true;
      const formData = new FormData();
      formData.append('status', 0);
      this.$axios.post(`societyadmin/bills/house/${item.id}/updateStatus`, formData)
        .then((res) => {
          this.rejectConfirmModal.loading = false;
          if (res.data.status) {
            this.rejectConfirmModal.open = false;
            this.approvalModal.open = false;
            this.setAlert({ showAlert: true, message: 'Payment request rejected. Bill set back to Pending.', variant: 'orange accent-3', timeout: 3000 });
            this.getBills();
          } else {
            this.setAlert({ showAlert: true, message: res.data.message, variant: 'red accent-3', timeout: 3000 });
            this.rejectConfirmModal.open = false;
          }
        }).catch(() => {
          this.rejectConfirmModal.loading = false;
          this.setAlert({ showAlert: true, message: 'Something went wrong.', variant: 'red accent-3', timeout: 3000 });
        });
    },

    openDocument(item) {
      // item is a document_url string for bill payment receipts
      this.mediaItems_bill = [{
        url: this.base_url + '/uploads/' + item,
        type: 'image',
        name: item.split('/').pop(),
      }];
      this.mediaIndex_bill = 0;
      this.imageDialog = true;
    },

    downloadReceipt(receipt_path) {
      const url = this.base_url + '/uploads/' + receipt_path;
      // Download programmatically
      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.setAttribute("download", receipt_path.split('/').pop());
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch(err => {
          console.error("Failed to download receipt:", err);
          window.open(url, '_blank');
        });
    },

    updateBillStatus(item, value, ref_id = '', payment_method = '', paid_date = null, remarks = '') {
      // Show loader during status update API call
      this.loading = true;
      const formData = new FormData();
      formData.append("status", value);
      if (ref_id) formData.append("ref_id", ref_id);
      if (payment_method) formData.append("payment_method", payment_method);
      if (paid_date) formData.append("paid_date", paid_date);
      if (remarks) formData.append("remarks", remarks);


      const url = `societyadmin/bills/house/${item.id}/updateStatus`;
      this.$axios
        .post(url, formData)
        .then((response) => {
          const data = response.data;
          if (data.status) {
            this.setAlert({
              showAlert: true,
              message: data.message,
              variant: "green accent-4",
              timeout: 2000,
            });
            this.getBills();
          } else {
            this.handleErrors(data);
          }
        });
    },

    toggleStatusMenu(event, item) {
      // Approval Request: dropdown not allowed
      if (item.payment_status == 2) return;

      if (this.statusMenu.open && this.statusMenu.item === item) {
        this.statusMenu.open = false;
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      this.statusMenu = {
        open: true,
        x: rect.left,
        y: rect.bottom + 4,
        item,
      };
    },

    selectStatus(item, value) {
      this.statusMenu.open = false;
      if (!item) return;

      // If item is already the selected status (and not cancelled), do nothing
      if (!item.is_cancelled && item.payment_status == value) return;

      // Logic: 
      // 1. If we are setting to Completed (1), always open the payment modal
      // 2. If we are setting to Pending (0), update directly
      
      if (value == 1) {
        this.paymentErrors = { payment_method: '', remarks: '' };
        this.paymentModal = {
          open: true, 
          item,
          ref_id: '', 
          payment_method: '', 
          remarks: '',
          paid_date: moment().format('YYYY-MM-DD'),
        };
      } else {
        // This covers reverting from Cancelled/Completed to Pending
        this.updateBillStatus(item, value, '', '');
      }
      //  this.updateBillStatus(item, value, '', '');
    },

    submitPaymentModal() {
      const { item, ref_id, payment_method, paid_date, remarks } = this.paymentModal;

      // ── Validation ──────────────────────────────
      this.paymentErrors = { payment_method: '', remarks: '' };
      let hasError = false;
      if (!payment_method) {
        this.paymentErrors.payment_method = 'Payment method is required';
        hasError = true;
      }
      if (!remarks || !remarks.trim()) {
        this.paymentErrors.remarks = 'Remark is required';
        hasError = true;
      }
      if (hasError) return;

      // Combine selected date with current time → UTC ms timestamp
    const paid_date_utc = paid_date
      ? moment(paid_date).startOf('day').utc().valueOf()
      : moment().startOf('day').utc().valueOf();
      this.paymentModal.open = false;
      this.updateBillStatus(item, 1, ref_id, payment_method, paid_date_utc, remarks);
    },

    openEditBill(item) {
      this.editModal.item = item;
      this.editModal.amount = item.amount || 0;
      this.editModal.due_on = item.due_on ? moment(item.due_on).format('YYYY-MM-DD') : null;
      this.editModal.late_fee_amount = item.late_fee_amount || 0;
      this.editModal.discount_amount = item.discount_amount || '';
      this.editModal.additional_charge_amount = item.additional_charge_amount || '';
      this.editModal.additional_charge_description = item.additional_charge_description || '';
      this.editModal.open = true;
    },

    submitEditBill() {
      // Frontend validation
      this.editErrors = {
        amount: '',
        due_on: '',
        late_fee_amount: '',
        discount_amount: '',
        additional_charge_amount: '',
        additional_charge_description: ''
      };
      let hasErr = false;
      
      if (!this.editModal.amount || Number(this.editModal.amount) <= 0) {
        this.editErrors.amount = 'Amount is required and must be greater than 0';
        hasErr = true;
      }
      if (!this.editModal.due_on) {
        this.editErrors.due_on = 'Due date is required';
        hasErr = true;
      }
      
      let amountVal = Number(this.editModal.amount) || 0;
      
      if (this.editModal.late_fee_amount === '' || this.editModal.late_fee_amount === null || this.editModal.late_fee_amount === undefined) {
        this.editErrors.late_fee_amount = 'Late fee is required';
        hasErr = true;
      } else if (Number(this.editModal.late_fee_amount) < 0) {
        this.editErrors.late_fee_amount = 'Late fee cannot be negative';
        hasErr = true;
      } else if (Number(this.editModal.late_fee_amount) >= amountVal) {
        this.editErrors.late_fee_amount = 'Late fee must be less than the bill amount';
        hasErr = true;
      }
      
      if (this.editModal.discount_amount !== '' && this.editModal.discount_amount !== null && this.editModal.discount_amount !== undefined) {
        if (Number(this.editModal.discount_amount) < 0) {
          this.editErrors.discount_amount = 'Discount cannot be negative';
          hasErr = true;
        } else if (Number(this.editModal.discount_amount) >= amountVal) {
          this.editErrors.discount_amount = 'Discount must be less than the bill amount';
          hasErr = true;
        }
      }
      
      if (this.editModal.additional_charge_amount !== '' && this.editModal.additional_charge_amount !== null && this.editModal.additional_charge_amount !== undefined) {
        if (Number(this.editModal.additional_charge_amount) < 0) {
          this.editErrors.additional_charge_amount = 'Charge amount cannot be negative';
          hasErr = true;
        } else if (Number(this.editModal.additional_charge_amount) >= amountVal) {
          this.editErrors.additional_charge_amount = 'Additional charge must be less than the bill amount';
          hasErr = true;
        }
      }
      
      if (Number(this.editModal.additional_charge_amount) > 0 && !this.editModal.additional_charge_description) {
        this.editErrors.additional_charge_description = 'Description is required when charge amount is added';
        hasErr = true;
      }

      if (hasErr) return;

      this.loading = true;
      const data = {
        amount: this.editModal.amount,
        due_on: this.editModal.due_on ? moment(this.editModal.due_on).startOf('day').utc().valueOf() : null,
        late_fee_amount: this.editModal.late_fee_amount || 0,
        discount_amount: this.editModal.discount_amount || 0,
        additional_charge_amount: this.editModal.additional_charge_amount || 0,
        additional_charge_description: this.editModal.additional_charge_description || ''
      };
      this.$axios.put(`societyadmin/bills/house/${this.editModal.item._id || this.editModal.item.id}/edit`, data).then(res => {
        this.loading = false;
         const data = res.data;
         console.log(data)
        if (data.status) {
          console.log(data.message)
              this.setAlert({
              showAlert: true,
              message: data.message,
              variant: "green accent-4",
              timeout: 2000,
            });

          this.editModal.open = false;
          this.getBills();
        } else {
          this.showError(res.data.message);
        }
      }).catch(err => {
        console.log(err);
        this.loading = false;
       
      });
    },

    openCancelBill(item) {
      this.cancelModal.item = item;
      this.cancelModal.reason = '';
      this.cancelModal.error = '';
      this.cancelModal.open = true;
    },

    submitCancelBill() {
      if (!this.cancelModal.reason) {
        this.cancelModal.error = "Reason is required";
        return;
      }
      this.cancelModal.error = '';
      this.loading = true;

      this.$axios.put(`societyadmin/bills/house/${this.cancelModal.item._id || this.cancelModal.item.id}/cancel`, { cancel_reason: this.cancelModal.reason }).then(res => {
        this.loading = false;
        const data = res.data;
        if (data.status) {
        
              this.setAlert({
              showAlert: true,
              message: data.message,
              variant: "green accent-4",
              timeout: 2000,
            });

          this.cancelModal.open = false;
          this.getBills();
        } else {
          this.showError(res.data.message);
        }
      }).catch(err => {
        console.log(err);
        this.loading = false;
        // this.showError("Something went wrong");
      });
    },

    getBills() {
      this.loading = true;
      let month, year;
      if (this.date) {
        month = moment(this.date, "YYYY-MM").get("month") + 1;
        year = moment(this.date, "YYYY-MM").get("year");
      }

      let url = `societyadmin/bills/house/bills?society_id=${this.user.id}&perPage=${this.itemsPerPage}&page=${this.page}`;
      if (this.date) url += `&month=${month}&year=${year}`;
      if (this.search) url += `&search=${this.search}`;
      if (this.paymentStatus !== "" && this.paymentStatus !== null) {
          url += `&payment_status=${this.paymentStatus}`;
      }

      this.$axios
        .get(url)
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.bills = data.housebills.data || data.housebills || [];
            this.totalRecords =
              data.total ||
              data.Total ||
              (data.housebills && data.housebills.total) ||
              0;

            // Update summary
            if (data.summary) {
              this.summary = {
                total_billed: data.summary.total_billed || 0,
                collected: data.summary.collected || 0,
                pending: data.summary.pending || 0,
                overdue: data.summary.overdue || 0,
              };
            } else {
              this.calculateStats();
            }
          } else {
            this.showError(data.message);
            if (data.type == "invalid") {
              localStorage.removeItem("user");
              this.$router.push("/societyadmin/login");
            }
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    calculateStats() {
      // Fallback: calculate stats from current page data
      const pending = this.bills.filter(
        (b) => b.payment_status == 0 || b.payment_status == "0",
      ).length;
      const totalAmount = this.bills.reduce(
        (sum, b) => sum + Number(b.amount || 0),
        0,
      );

      this.summary = {
        total_billed: totalAmount,
        collected: 0,
        pending: pending,
        overdue: 0,
      };
    },
    formatCurrency(val) {
      if (!val || val == 0) return "₹0";
      if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
      if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
      return `₹${Number(val).toLocaleString()}`;
    },
    exportToExcel() {
      let month, year;
      if (this.date) {
        month = moment(this.date, "YYYY-MM").get("month") + 1;
        year = moment(this.date, "YYYY-MM").get("year");
      }

      this.$axios
        .get("societyadmin/export/bills", {
          params: {
            society_id: this.user.id,
            month,
            year,
            search: this.search,
          },
          responseType: "blob",
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `House_Bills_${moment().format("YYYY-MM-DD")}.xlsx`,
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(() => {
          this.showError("Failed to export data");
        });
    },

    getFormattedDate(date) {
      return date ? moment(date).format("DD MMM, YYYY · hh:mm A") : "N/A";
    },

    sessionStorage(search) {
      if (!search) sessionStorage.removeItem("bill_search");
      else sessionStorage.setItem("bill_search", search);
    },

    handleErrors(data) {
      if (data.errors) {
        this.showError(data.errors[0].message || data.message);
      } else {
        this.showError(data.message);
      }
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};
    this.search = sessionStorage.getItem("bill_search") || "";
    // Setting date triggers the watch, which calls getBills()
    this.date = moment().format("YYYY-MM");

    // Close status dropdowns when clicking outside
    document.addEventListener("click", () => {
      this.statusMenu.open = false;
    });
  },
};
</script>

<style scoped>
.status-pill-wrap {
  position: relative;
  display: inline-block;
}
.bill-list-container {
  padding: 24px;
}

.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}

/* ── Stats Row ── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 4px;
}

@media (max-width: 768px) {
  .stats-row {
    grid-template-columns: 1fr;
  }
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

@media (max-width: 600px) {
  .bill-list-container {
    padding: 12px;
  }
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  .header-controls {
    width: 100%;
  }
  .header-controls >>> .premium-date-wrap {
    flex: 1;
  }
}

.custom-date-trigger {
  display: flex;
  align-items: center;
  height: 38px;
  padding: 0 12px;
  background: #ffffff;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2, #374151);
  cursor: pointer;
  transition: all 0.2s;
  min-width: 140px;
}
.custom-date-trigger:hover {
  border-color: var(--brand, #0d9972);
  background: var(--surface2, #f8fafc);
}

.icon-action-btn {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 1px solid var(--border, #e2e8f0);
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tx2, #374151);
  cursor: pointer;
  transition: all 0.2s;
}
.icon-action-btn:hover {
  border-color: var(--brand, #0d9972);
  color: var(--brand, #0d9972);
  background: var(--surface2, #f8fafc);
}

/* ── Cells ── */
.member-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.member-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--tx, #111827);
}
.house-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px;
  border-radius: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  color: var(--tx, #111827);
  width: fit-content;
}
.house-number-badge .flat-prefix {
  color: var(--tx3, #6b7280);
}
.house-number-badge .house-num {
  color: var(--tx3, #6b7280);
}

.bill-title-text {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--tx2, #374151);
}
.amount-text {
  font-weight: 700;
  color: var(--tx, #111827);
  font-family: "JetBrains Mono", monospace;
  font-size: 13px;
}
.date-text {
  font-size: 12.5px;
  color: var(--tx3, #6b7280);
}

/* Status Pill */
.status-pill-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 7px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
}
.status-pending {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}
.status-pending .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f59e0b;
  flex-shrink: 0;
}
.status-completed {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}
.status-completed .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
}
.status-pill-btn:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}

/* Paid Date & Doc */
.paid-date-cell {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.doc-view-btn {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--surface2, #f8fafc);
  color: var(--brand, #0d9972);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border, #e2e8f0);
  cursor: pointer;
  transition: all 0.2s;
}
.doc-view-btn:hover {
  background: var(--brand, #0d9972);
  color: #fff;
  border-color: var(--brand, #0d9972);
}


.info-item label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--tx4, #9ca3af);
  letter-spacing: 0.05em;
}
.info-item span {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2, #374151);
}
</style>

<style>
/* Fixed-position status dropdown portal — outside scoped so it renders above everything */
.status-dropdown-portal {
  position: fixed;
  z-index: 9999;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.14);
  min-width: 155px;
  overflow: hidden;
  animation: sdpFadeIn 0.15s ease;
}

@keyframes sdpFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.status-dropdown-portal .status-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx2, #374151);
  cursor: pointer;
  transition: background 0.12s;
}
.status-dropdown-portal .status-option:hover {
  background: var(--surface2, #f8fafc);
}
.status-dropdown-portal .status-option.active {
  background: var(--surface2, #f8fafc);
  color: var(--tx, #111827);
}
.status-dropdown-portal .status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cm2-dark .info-item span {
  color: #cbd5e1;
}
.info-item .amount {
  color: var(--brand, #0d9972) !important;
  font-family: "JetBrains Mono", monospace;
  font-size: 14px;
}

.cm2-field-select {
  margin-bottom: 4px;
}
.cm2-footer {
  display: flex;
  gap: 10px;
  padding: 14px 22px 20px;
  justify-content: flex-end;
}
.cm2-btn-cancel {
  padding: 9px 18px;
  border-radius: 10px;
  border: 1px solid var(--border, #e2e8f0);
  background: transparent;
  color: var(--tx2, #374151);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: "Instrument Sans", sans-serif;
}
.cm2-btn-cancel:hover {
  background: var(--surface2, #f8fafc);
}
.cm2-btn-save {
  padding: 9px 20px;
  border-radius: 10px;
  border: none;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: "Instrument Sans", sans-serif;
}
.cm2-btn-save:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
}

@keyframes cm2FadeIn {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Export Button */
.export-btn {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 0 10px 0 10px !important
}

.export-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  filter: brightness(1.1);
}

.export-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}
.field-error {
  margin-top: 4px;
  font-size: 12px;
  color: #ef4444;
  line-height: 1.3;
}
.status-cancelled {
  background: rgba(239, 68, 68, 0.1) !important;
  color: #ef4444 !important;
}
.status-pill-btn .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
    flex-shrink: 0;

}

.status-cancelled .dot {
  background-color: #ef4444; 
}


.btn-icon-ghost {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: transparent; /* No background */
  color: #10b981 !important;          /* Icon color */
  display: flex;
  align-items: center;
  justify-content: center;
  border: none !important;  /* Border kadhva mate */
  cursor: pointer;
  transition: all 0.2s;
}

.btn-icon-ghost:hover {
  background: rgba(16, 185, 129, 0.1); /* Halka background color */
}

.btn-icon-approve {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: transparent;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}
.btn-icon-approve .v-icon {
  z-index: 2;
}
.btn-icon-approve .hover-bg {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: transparent;
  transition: all 0.2s ease;
  z-index: 1;
}
.status-approval-request {
  background: #fff3cd;
  color: #856404;
}
/* ── Approval Modal Body ── */
.apv-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.apv-header-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(245, 158, 11, 0.06);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 10px;
  margin-bottom: 2px;
}
.apv-badge-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: rgba(245, 158, 11, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.apv-badge-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--tx, #111827);
  margin: 0 !important;
}
.apv-badge-sub {
  font-size: 11.5px;
  color: var(--tx3, #6b7280);
  margin: 0;
}

/* 2×2 grid */
.apv-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.apv-mini-card {
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 9px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.apv-mini-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--tx4, #9ca3af);
  display: block;
  /* margin-bottom: 2px; */
}
.apv-mini-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx, #111827);
}
.apv-amount-green {
  font-size: 15px;
  font-weight: 700;
  color: #10b981;
  font-family: "JetBrains Mono", monospace;
}

/* Remark row */
.apv-remark-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 9px;
  padding: 10px 12px;
}
.apv-remark-text {
  font-size: 13px;
  color: var(--tx2, #374151);
  margin: 2px 0 0;
}

/* Screenshot box */
.apv-screenshot-box {
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 10px;
  overflow: hidden;
}
.apv-screenshot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: var(--surface2, #f8fafc);
  border-bottom: 1px solid var(--border, #e2e8f0);
}
.apv-screenshot-header-left {
  display: flex;
  align-items: center;
  gap: 6px;
}
.apv-view-full-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: #3b82f6;
  font-weight: 600;
  padding: 0;
}
.apv-screenshot-preview {
  position: relative;
  height: 180px;
  background: #111;
  cursor: zoom-in;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.apv-screenshot-img {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
}
.apv-screenshot-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: background 0.2s;
}
.apv-screenshot-preview:hover .apv-screenshot-overlay {
  background: rgba(0, 0, 0, 0.45);
}
.apv-zoom-label {
  font-size: 12px;
  color: white;
  opacity: 0;
  transition: opacity 0.2s;
}
.apv-screenshot-preview:hover .apv-zoom-label {
  opacity: 1;
}
.apv-screenshot-preview:hover .apv-screenshot-overlay .v-icon {
  opacity: 1 !important;
}
.apv-screenshot-overlay .v-icon {
  opacity: 0;
  transition: opacity 0.2s;
}

/* No screenshot */
.apv-no-screenshot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px;
  background: var(--surface2, #f8fafc);
  border: 1px dashed var(--border, #e2e8f0);
  border-radius: 10px;
  font-size: 13px;
  color: var(--tx3, #6b7280);
}

/* Reject section */
.apv-reject-section {
  margin-top: 4px;
}

.action-btn {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* border: 1px solid #e2e8f0; */
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.action-btn .v-icon {
  z-index: 2;
}

.action-btn .hover-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: transparent;
  transition: all 0.2s ease;
  z-index: 1;
}

.actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

</style>
